import React, { useCallback, useEffect, useState } from 'react'
import { Box, Grid, Typography } from '@material-ui/core'
import queryString from 'query-string';
import moment from 'moment';
import { Tab, Tabs } from 'components/shared/Tabs';
import { monitoringTabs, REPORT_TYPE } from 'constants/constantVariables';
import { monitoringColumns } from 'constants/application';
import TableComponent from 'components/shared/TableComponent';
import AuthService from "tools/AuthService"
import MonitoringCommingSoon from './monitoringCommingSoon';
import MonitoringChangesDrawer from './monitoringChangesDrawer';
import MonitoringLedger from './monitoringLedger';
import MonitoringBulkSetup from './monitoringBulkSetup';
import MonitoringNotifications from './monitoringNotifications';
import { Icon } from 'styles';
import Button from '../shared/Button';
import strings from 'strings';
import './monitoringList.scss'

const MONITORING_STRING = strings.MONITORINGS
const MonitoringList = (props) => {
    const {
        history,
        location,
        getMonitoringAlerts,
        monitoringList = [],
        getMonitoringConfigurationList,
        monitoringConfigurationList = [],
        monitoringConfigurationHeaders = {},
        getMonitoringSummary,
        acknowledgeMonitoringAlert,
        acknowledgeMonitoringAlerts,
        addMonitoringAlertNote,
        hideMonitoringAlertNote,
        monitoringSummary,
        monitoringAlertHeaders = {},
        getMonitoringSeverityConfig,
        updateMonitoringSeverityConfig,
        getMonitoringFieldCatalog,
        getMonitoringWatchedFields,
        updateMonitoringWatchedFields,
        getMonitoringBusinesses,
        getMonitoringDevEnabled,
        createMonitoringTestAlert,
        runMonitoringNow,
        previewBulkMonitoring,
        applyBulkMonitoring,
        getMonitoringConfig,
        updateMonitoringConfig
    } = props

    const { monitoringEnabled, monitoringAdvancedEnabled } = AuthService.getClientFeatures()
    // Read-only users can view monitoring but must not edit schedules (matches the rest of the app).
    const isReadOnlyUser = AuthService.isReadOnlyUser()

    // The MONITORING_ADVANCED flag fully switches the experience: advanced clients see ONLY the redesigned
    // tabs (the legacy Alerts / All Monitoring UI disappears); everyone else keeps the legacy tabs. Indices
    // are resolved by URL so the Tab panels below can't drift as the visible set changes.
    const tabs = monitoringAdvancedEnabled
        ? [
            { label: 'Alerts', url: '?tab=alerts-new' },
            { label: 'All Monitoring', url: '?tab=monitoring-new' },
            { label: 'Notifications', url: '?tab=notifications' }]
        : monitoringTabs;
    const tabIndex = (url) => tabs.findIndex(t => t.url === url);
    const ALERTS_INDEX = tabIndex('?tab=alerts');
    const ALL_MONITORING_INDEX = tabIndex('?tab=monitoring');
    const ALERTS_NEW_INDEX = tabIndex('?tab=alerts-new');
    const MONITORING_NEW_INDEX = tabIndex('?tab=monitoring-new');
    const NOTIFICATIONS_INDEX = tabIndex('?tab=notifications');

    const currentIndex = tabs.findIndex(t => t.url === `?tab=${queryString.parse(location.search).tab}`)
    const [tabValue, setTabValue] = useState(currentIndex > 0 ? currentIndex : 0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedAlertItem, setSelectedAlertItem] = useState(null);

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
        history.replace(tabs[newValue].url, 'tab');
    };

    const openAlert = (item) => {
        setSelectedAlertItem(item);
        setDrawerOpen(true);
        const params = queryString.parse(location.search);
        params.alertId = item.uuid;
        history.replace({ search: queryString.stringify(params) });
    };

    const closeAlert = () => {
        setDrawerOpen(false);
        const params = queryString.parse(location.search);
        delete params.alertId;
        history.replace({ search: queryString.stringify(params) });
    };

    // Redesign-preview: the ledger drives its own server-side query (filters + paging) and refreshes
    // the summary tiles alongside it. Memoized so it's a stable prop (the ledger keys an effect on it).
    const queryLedger = useCallback((params = {}) => {
        const { page = 0, limit = 10, reportType, acknowledged, search, from, to, businessName } = params
        const filters = {}
        if (reportType) filters.reportType = reportType
        if (acknowledged !== undefined && acknowledged !== null) filters.acknowledged = acknowledged
        if (search) filters.search = search
        if (from) filters.from = from
        if (to) filters.to = to
        if (businessName) filters.businessName = businessName
        const request = getMonitoringAlerts({ page, limit }, filters)
        // Summary tiles are an optional enhancement; swallow its rejection (e.g. endpoint not yet deployed)
        // so a missing summary never surfaces as an unhandled rejection while the alert query still succeeds.
        if (getMonitoringSummary) Promise.resolve(getMonitoringSummary()).catch(() => {})
        return request
    }, [getMonitoringAlerts, getMonitoringSummary])

    useEffect(() => {
        // tab 0 = current Alerts (unfiltered). tab 2 = redesign preview, which drives its own
        // filtered/paged query from inside the ledger, so we don't load it here.
        if (monitoringEnabled && tabValue === ALERTS_INDEX) {
            getMonitoringAlerts().finally(() => { })
        }
    }, [getMonitoringAlerts, monitoringEnabled, tabValue, ALERTS_INDEX])

    // Auto-open alert from URL
    useEffect(() => {
        const { alertId } = queryString.parse(location.search);
        if (alertId && monitoringList.length > 0 && !drawerOpen) {
            const item = monitoringList.find(a => a.uuid === alertId);
            if (item) {
                setSelectedAlertItem(item);
                setDrawerOpen(true);
            }
        }
    }, [monitoringList])

    useEffect(() => {
        // Both the legacy "All Monitoring" table (tab 1) and the advanced "All Monitoring (new)" bulk setup
        // (last tab) read the same configuration list.
        if (monitoringEnabled && (tabValue === ALL_MONITORING_INDEX || tabValue === MONITORING_NEW_INDEX)) {
            getMonitoringConfigurationList();
        }
    }, [getMonitoringConfigurationList, monitoringEnabled, tabValue, ALL_MONITORING_INDEX, MONITORING_NEW_INDEX]);

    const getTimeDiff = (start, end) => {
        const startDate = moment(start);
        const endDate = moment(end);
        const duration = moment.duration(endDate.diff(startDate));

        if (duration.years() > 0) {
            return `(${duration.years()} year${duration.years() > 1 ? 's' : ''})`
        } else if (duration.months() > 0) {
            return `(${duration.months()} month${duration.months() > 1 ? 's' : ''})`
        } else if (duration.days() > 0) {
            return `(${duration.days()} day${duration.days() > 1 ? 's' : ''})`
        }
        return '';
    }

    if (!monitoringEnabled) {
        return (
            <MonitoringCommingSoon />
        )
    }

    return (
        <Box>
            <h4>{MONITORING_STRING.MONITORING}</h4>

            <Tabs
                data={tabs}
                value={tabValue}
                onChange={handleChange}
            />
            {!monitoringAdvancedEnabled && (<>
            <Tab value={tabValue} index={ALERTS_INDEX}>
                <Box className='tabPanel'>
                    <h5>{MONITORING_STRING.MONITORING_ALERTS}</h5>
                    {
                        monitoringList && monitoringList?.length > 0 && monitoringList?.map((item) => {
                            return (
                                <Box key={item.uuid} className="monitoringAlertCard">
                                    <Grid container>
                                        <Grid item md={2}>
                                            <Typography
                                                className="monitoringAlertCard__business"
                                                onClick={() => history.push(`/applicant/${item.applicationId}`)}
                                            >
                                                {item.businessName}
                                            </Typography>
                                        </Grid>
                                        <Grid item md={4}>
                                            <Box sx={{ display: "flex" }}>
                                                <Icon
                                                    className='mr-3'
                                                    title='Termination'
                                                    fileName='monitoringAlert.svg'
                                                />
                                                <Box>
                                                    <Typography>{item.description}</Typography>
                                                    <Typography className="monitoringMeta">{moment(item.createdAt).format('MMM DD, YYYY')}</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item md={4}>
                                            <Typography>{MONITORING_STRING.START_END_MONITORING}</Typography>
                                            <Typography className="monitoringMeta">{moment(item.monitoringStartDate).format('MMM DD, YYYY')} to {moment(item.monitoringEndDate).format('MMM DD, YYYY')} <span style={{ fontWeight: "bold" }}>{getTimeDiff(item.monitoringStartDate, item.monitoringEndDate)}</span></Typography>
                                        </Grid>
                                        <Grid item md={2}>
                                            <Button
                                                variant="text"
                                                onClick={() => openAlert(item)}
                                            >
                                                {strings.VIEW_MORE}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )
                        })
                    }
                </Box>
            </Tab>
            <Tab value={tabValue} index={ALL_MONITORING_INDEX}>
                <Box className='tabPanel'>
                    <h5>{MONITORING_STRING.MONITORING}</h5>
                    <TableComponent
                        className='custom-table'
                        data={monitoringConfigurationList}
                        keyField='applicationId'
                        sort={true}
                        columns={monitoringColumns}
                        totalRecords={monitoringConfigurationHeaders['x-pagination-total-records'] || 0}
                        totalPages={monitoringConfigurationHeaders['x-pagination-total-pages'] || 0}
                        onChangePagination={(pagination) => getMonitoringConfigurationList(pagination)}
                        loading={false}
                        currentPage={monitoringConfigurationHeaders['x-pagination-current-page-num'] || 0}
                    />
                </Box>
            </Tab>
            </>)}
            {monitoringAdvancedEnabled &&
                <Tab value={tabValue} index={ALERTS_NEW_INDEX}>
                    <Box className='tabPanel'>
                        <MonitoringLedger
                            monitoringList={monitoringList}
                            history={history}
                            summary={monitoringSummary}
                            alertHeaders={monitoringAlertHeaders}
                            onQuery={queryLedger}
                            onAcknowledge={acknowledgeMonitoringAlert}
                            onAcknowledgeMany={acknowledgeMonitoringAlerts}
                            onAddNote={addMonitoringAlertNote}
                            onHideNote={hideMonitoringAlertNote}
                            getSeverityConfig={getMonitoringSeverityConfig}
                            updateSeverityConfig={updateMonitoringSeverityConfig}
                            getFieldCatalog={getMonitoringFieldCatalog}
                            getWatchedFields={getMonitoringWatchedFields}
                            updateWatchedFields={updateMonitoringWatchedFields}
                            getBusinesses={getMonitoringBusinesses}
                            getDevEnabled={getMonitoringDevEnabled}
                            onCreateTestAlert={createMonitoringTestAlert}
                            readOnly={isReadOnlyUser}
                        />
                    </Box>
                </Tab>}
            {monitoringAdvancedEnabled &&
                <Tab value={tabValue} index={MONITORING_NEW_INDEX}>
                    <Box className='tabPanel'>
                        <MonitoringBulkSetup
                            configs={monitoringConfigurationList}
                            headers={monitoringConfigurationHeaders}
                            onQuery={(pagination, filters) => getMonitoringConfigurationList(pagination, filters)}
                            onPreview={previewBulkMonitoring}
                            onApply={applyBulkMonitoring}
                            getConfig={getMonitoringConfig}
                            updateConfig={updateMonitoringConfig}
                            getDevEnabled={getMonitoringDevEnabled}
                            onRunNow={runMonitoringNow}
                            readOnly={isReadOnlyUser}
                            onApplied={() => getMonitoringConfigurationList({ page: Number(monitoringConfigurationHeaders['x-pagination-current-page-num']) || 0 })}
                        />
                    </Box>
                </Tab>}
            {monitoringAdvancedEnabled &&
                <Tab value={tabValue} index={NOTIFICATIONS_INDEX}>
                    <Box className='tabPanel'>
                        <MonitoringNotifications history={history} />
                    </Box>
                </Tab>}
            <MonitoringChangesDrawer
                open={drawerOpen}
                onClose={closeAlert}
                title="Change Details"
                items={selectedAlertItem?.changes || []}
                applicationId={selectedAlertItem?.applicationId}
                entityUuid={selectedAlertItem?.entityUuid}
                entityType={selectedAlertItem?.entityType}
                searchId={selectedAlertItem?.searchId}
                reportType={selectedAlertItem?.reportType || (selectedAlertItem?.description?.toLowerCase().includes('background') ? REPORT_TYPE.LEXIS_BACKGROUND : REPORT_TYPE.UCC)}
                history={history}
            />
        </Box>
    )
}

export default MonitoringList
